import { Close } from "@components/buttons/close";
import { InputCurrency } from "@components/forms/input-currency";
import { Select } from "@components/forms/select";
import { LoadingSkeleton } from "@components/skeleton-loading";
import {
  ICustomerBalanceDTO,
  IPayoutAccountOptionDTO,
  IPayoutMethodFeeDTO,
  IPayoutMethodsConfigDTO,
} from "@hooks/dtos/IPayoutDTO";
import { Masks } from "@hooks/functions/Masks/Masks";
import { getPayoutAccountType } from "@hooks/functions/Payout";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { api } from "@service/api";
import { useEffect, useState } from "react";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const masks = new Masks();

function calculateFee(amount: number, fee?: IPayoutMethodFeeDTO) {
  if (!fee || amount <= 0) return 0;

  const range = fee.table.find((item) => {
    return (
      amount >= Number(item.minAmount || 0) &&
      amount <= Number(item.maxAmount || 0)
    );
  });

  if (!range || !range.value || fee.type === "default") return 0;

  if (fee.type === "percent") {
    return (amount * Number(range.value)) / 100;
  }

  if (fee.type === "flat") {
    return Number(range.value);
  }

  return 0;
}

export function ModalPayoutRequest({ isOpen, onClose, onCreated }: IProps) {
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  const [animate, setAnimate] = useState(false);
  const [balance, setBalance] = useState<ICustomerBalanceDTO>({
    available: 0,
    pendingRelease: 0,
    total: 0,
  });
  const [selectedAccount, setSelectedAccount] = useState<number | "">("");
  const [payoutAccounts, setPayoutAccounts] = useState<
    IPayoutAccountOptionDTO[]
  >([]);
  const [payoutMethodsConfig, setPayoutMethodsConfig] =
    useState<IPayoutMethodsConfigDTO>({});
  const [value, setValue] = useState(0);
  const [isLoading, setIsLoading] = useState({
    accounts: false,
    balance: false,
    methods: false,
    submit: false,
  });

  const activeMethodIds = Object.keys(payoutMethodsConfig);
  const activePayoutAccounts = payoutAccounts.filter((item) =>
    activeMethodIds.includes(String(item.payoutMethod)),
  );
  const account = activePayoutAccounts.find(
    (item) => item.id === selectedAccount,
  );
  const methodConfig = account
    ? payoutMethodsConfig[String(account.payoutMethod)]
    : undefined;
  const withdrawMin = Number(methodConfig?.config?.withdraw?.min || 0);
  const withdrawMax = Number(methodConfig?.config?.withdraw?.max || 0);
  const internalFee = calculateFee(value, methodConfig?.fees?.internal);
  const externalFee = calculateFee(value, methodConfig?.fees?.external);
  const totalFee = internalFee + externalFee;
  const netValue = Math.max(value - totalFee, 0);
  const isBelowMin =
    !!account && withdrawMin > 0 && value > 0 && value < withdrawMin;
  const isAboveMax = !!account && withdrawMax > 0 && value > withdrawMax;
  const canSubmit =
    !!account &&
    value > 0 &&
    value <= balance.available &&
    !isBelowMin &&
    !isAboveMax &&
    !isLoading.accounts &&
    !isLoading.methods;
  const payoutAccountOptions = activePayoutAccounts.map((item) => {
    const type = getPayoutAccountType(item.type);

    return {
      value: item.id,
      name: `${item.label || "Conta de saque"} - ${type.text} - ${
        item.accountKey
      }`,
    };
  });

  function handleClose() {
    setAnimate(false);
    setSelectedAccount("");
    setValue(0);
    setTimeout(() => onClose(), 300);
  }

  function handleEscape(evt: KeyboardEvent) {
    if (evt.key === "Escape" || evt.key === "Esc" || evt.keyCode === 27) {
      handleClose();
    }
  }

  async function getCustomerBalance() {
    setIsLoading((prev) => ({ ...prev, balance: true }));

    try {
      const result = await api.get("/customer/dashboard/financial-summary");
      setBalance(result.data);
    } catch (err: any) {
      pushToastify({
        message:
          err?.response?.data?.message ??
          "Houve um erro ao consultar seu saldo.",
        type: "error",
        theme: "dark",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, balance: false }));
    }
  }

  async function getPayoutAccounts() {
    setIsLoading((prev) => ({ ...prev, accounts: true }));

    try {
      const result = await api.get("/customer/payout/accounts?status=a");
      setPayoutAccounts(result.data.accounts || []);
    } catch (err: any) {
      setPayoutAccounts([]);
      pushToastify({
        message:
          err?.response?.data?.message ??
          "Houve um erro ao consultar suas contas de saque.",
        type: "error",
        theme: "dark",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, accounts: false }));
    }
  }

  async function getPayoutMethodsConfig() {
    setIsLoading((prev) => ({ ...prev, methods: true }));

    try {
      const result = await api.get("/customer/payout/methods/config");
      setPayoutMethodsConfig(result.data || {});
    } catch (err: any) {
      setPayoutMethodsConfig({});
      pushToastify({
        message:
          err?.response?.data?.message ??
          "Houve um erro ao consultar os métodos de saque.",
        type: "error",
        theme: "dark",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, methods: false }));
    }
  }

  async function handleSubmit() {
    if (!canSubmit) {
      pushToastify({
        message:
          "Informe uma conta ativa e um valor válido para solicitar o saque.",
        type: "warning",
        theme: "dark",
      });
      return;
    }

    setIsLoading((prev) => ({ ...prev, submit: true }));

    try {
      await api.post("/customer/payout/request", {
        account: selectedAccount,
        payoutMethod: account?.payoutMethod,
        value,
      });

      pushToastify({
        message: "Solicitação de saque criada com sucesso.",
        type: "success",
        theme: "dark",
      });
      onCreated();
      handleClose();
    } catch (err: any) {
      pushToastify({
        message:
          err?.response?.data?.message ?? "Houve um erro ao solicitar o saque.",
        type: "error",
        theme: "dark",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, submit: false }));
    }
  }

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setAnimate(true), 10);
      document.addEventListener("keydown", handleEscape);
      getCustomerBalance();
      getPayoutAccounts();
      getPayoutMethodsConfig();

      return () => {
        document.removeEventListener("keydown", handleEscape);
      };
    }

    setAnimate(false);
  }, [isOpen]);

  useEffect(() => {
    if (
      selectedAccount !== "" &&
      !activePayoutAccounts.some((item) => item.id === selectedAccount)
    ) {
      setSelectedAccount("");
    }
  }, [payoutAccounts, payoutMethodsConfig, selectedAccount]);

  return (
    <>
      <div
        className={`modal fade ${animate && isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
        aria-labelledby="payoutRequestModalLabel"
        aria-hidden={!isOpen}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title fw-bold" id="payoutRequestModalLabel">
                Solicitar Saque
              </h5>
              <Close onPress={handleClose} />
            </div>

            <div className="modal-body">
              <div className="border rounded bg-light p-3 mb-3">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
                  <div>
                    <small className="text-muted">Saldo disponível</small>
                    <div
                      className="fs-3 fw-bold text-success lh-sm"
                      title="Valor disponível para solicitar saque."
                    >
                      {isLoading.balance ? (
                        <LoadingSkeleton count={1} />
                      ) : (
                        masks.brlCurrency(balance.available)
                      )}
                    </div>
                  </div>
                  <span className="badge badge-success align-self-start align-self-md-center">
                    Disponível para saque
                  </span>
                </div>
              </div>

              <div className="border rounded p-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="fw-bold mb-0">Dados do saque</h6>
                  <small className="text-muted">
                    {account?.payoutMethodName || "Método ativo"}
                  </small>
                </div>

                <div className="row g-3">
                  <div className="col-md-7">
                    <Select
                      animated
                      label="Conta destino"
                      className="form-select form-control"
                      value={selectedAccount.toString()}
                      disabled={isLoading.accounts || isLoading.methods}
                      options={
                        isLoading.accounts || isLoading.methods
                          ? [{ value: "", name: "Carregando contas..." }]
                          : payoutAccountOptions.length
                            ? [...payoutAccountOptions]
                            : [{ value: "", name: "Nenhuma conta ativa" }]
                      }
                      onChange={(e) =>
                        setSelectedAccount(
                          e.target.value ? Number(e.target.value) : "",
                        )
                      }
                    />
                  </div>

                  <div className="col-md-5">
                    <InputCurrency
                      animated
                      label="Valor do saque"
                      prefix="R$"
                      value={value}
                      placeholder="0,00"
                      onChangeTextOut={(v: number) => setValue(v)}
                    />
                  </div>
                </div>

                {account && (
                  <div className="bg-light border rounded p-3">
                    <div className="d-flex flex-column flex-md-row justify-content-between gap-2">
                      <div>
                        <div className="fw-semibold">
                          {account.label || "Conta de saque"}
                        </div>
                        <div className="text-muted small">
                          {account.payoutMethodName} -{" "}
                          {getPayoutAccountType(account.type).text}
                        </div>
                      </div>
                      <div className="fw-semibold text-md-end">
                        {account.accountKey}
                      </div>
                    </div>
                    {methodConfig?.config?.withdraw && (
                      <div className="text-muted small mt-2">
                        Valor permitido: {masks.brlCurrency(withdrawMin || 0)}{" "}
                        até {masks.brlCurrency(withdrawMax || 0)}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="border rounded p-3 mt-3">
                <h6 className="fw-bold mb-3">Resumo da solicitação</h6>

                <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                  <span className="text-muted">Valor solicitado</span>
                  <strong>{masks.brlCurrency(value)}</strong>
                </div>
                <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                  <span className="text-muted">Taxa interna</span>
                  <span>{masks.brlCurrency(internalFee)}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                  <span className="text-muted">Taxa externa</span>
                  <span>{masks.brlCurrency(externalFee)}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center pt-3">
                  <span className="fw-semibold">Valor a receber</span>
                  <strong className="fs-5 text-success">
                    {masks.brlCurrency(netValue)}
                  </strong>
                </div>
              </div>

              {value > balance.available && (
                <div className="alert alert-warning mt-3 mb-0">
                  O valor informado ultrapassa o saldo disponível.
                </div>
              )}

              {isBelowMin && (
                <div className="alert alert-warning mt-3 mb-0">
                  O valor mínimo para este método é{" "}
                  {masks.brlCurrency(withdrawMin)}.
                </div>
              )}

              {isAboveMax && (
                <div className="alert alert-warning mt-3 mb-0">
                  O valor máximo para este método é{" "}
                  {masks.brlCurrency(withdrawMax)}.
                </div>
              )}
            </div>

            <div className="modal-footer d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
              <small className="text-muted">
                As taxas são descontadas do valor solicitado.
              </small>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={handleClose}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-success px-4"
                  disabled={!canSubmit || isLoading.submit}
                  onClick={handleSubmit}
                >
                  {isLoading.submit ? "Enviando..." : "Confirmar Solicitação"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`modal-backdrop fade ${animate && isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
      ></div>
    </>
  );
}
